'use strict';

import React from 'react';
import md5 from 'md5';
import parse from 'html-react-parser';
import _ from 'lodash';

import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';
import {DataDescriptorValues} from '~/shared/src/utilities/condition';
import {Style} from '~/shared/src/utilities/style';
import {
    formatBool,
    formatTimerule,
    formatValue
} from '~/shared/src/utilities/valueFormaters';
import {
    dateRenderer,
    doubleRenderer,
    durationRenderer,
    emailRenderer as emailRendererOrig,
    ibanRenderer as ibanRendererOrig,
    imageRenderer as imageRendererOrig,
    integerRenderer,
    jsonRenderer as jsonRendererOrig,
    linkRenderer as linkRendererOrig,
    phoneRenderer as phoneRendererOrig,
    RendererValueType,
    timeRenderer,
    timestampRenderer
} from '~/shared/src/utilities/valueRenderers';

import {
    appendMediaRuleIfNotExists,
    appendRule,
    getOrCreateMediaRule,
    getOrCreateStyleSheet,
    ruleExists
} from '~/frontend/src/utilities/cssHelpers';
import {
    getStatusColorStyleObject,
    getStatusDisplayName,
    getTextColorForBackgroundColor,
    htmlQuoteEncode,
    markFilterSearchesInText
} from '~/frontend/src/utilities/renderHelpers';
import {resolveConditionRecursive} from '~/frontend/src/utilities/dataUtils';


/**
 * Renderer for status coloring. Wraps the given value with a div applying the coloring.
 *
 * @param value            The value to be rendered in status color.
 * @param statusId         The StatusId to get the color for.
 * @param schema           The schema to get the status color from.
 * @param key              The key for the React Component.
 * @param id               The ID for the div.
 * @param className        Additional class names for the div.
 */
export const statusColoringRenderer = ( value : any, statusId : number, schema : Schema = global.schema, key : string = '', id : string = '', className : string = '', tabIndex? : number ) : JSX.Element => {
    const colorStyle = getStatusColorStyleObject( statusId, schema );

    let statusName;

    if ( statusId ) {
        statusName = metaModel[ schema ].getStatus( statusId )?.name ?? 'notdefined';
    }
    else {
        statusName = 'notdefined';
    }

    className = `status-${statusName} ${className}`;

    return <span key={key} id={id} className={`yn-status-renderer ${className}`} style={colorStyle} tabIndex={tabIndex}>{value}</span>;
}

/**
 * Renderer for value.
 *
 * @param {RendererValueType} type
 * @param {any} value
 * @param {object} [params={}]                      Any additionally needed params for getting the filter field.
 *                                                  Like valueType, searchString, filterValue, timezone, schema, showAdditionalRawNames, valueSetId, ...
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {JSX.Element | string | ( string | JSX.Element )[] | undefined}
 */
// TODO: refactor parameters to type, value, rawMode, params
// TODO: mapped type for params depending on type, see getValueEditor
export const renderValue = ( type : RendererValueType, value : any, params : any = {}, rawMode : boolean = false, unParsed : boolean = false ) : JSX.Element | string | ( string | JSX.Element )[] | undefined => {
    let retVal : JSX.Element | string | ( string | JSX.Element )[] | undefined,
        parts : string[] | undefined;

    switch ( type ) {
        case 'BOOLEAN':
            retVal = boolRenderer( value, rawMode, unParsed );
            break;

        case 'COLOR':
            retVal = colorRenderer( value, params.filterString, rawMode, unParsed );
            break;

        case 'COORDINATE':
            if ( value && !Array.isArray( value ) ) {
                value = [ value ];
            }
            retVal = coordinateRenderer( value, params.editMode, rawMode, unParsed );
            break;

        case 'CURRENCY':
            retVal = doubleRenderer( value, params.valueUnit, undefined, rawMode );
            break;

        case 'DATE':
            retVal = dateRenderer( value, params.timezone, rawMode );
            break;

        case 'DOUBLE':
            retVal = doubleRenderer( value, params.valueUnit, params.valuePrecision, rawMode );
            break;

        case 'DURATION':
            retVal = durationRenderer( value, rawMode );
            break;

        case 'EMAIL':
            retVal = emailRenderer( value, params.filterString, params.editMode, rawMode, unParsed );
            break;

        case 'IBAN':
            retVal = ibanRenderer( value, params.filterString, rawMode, unParsed );
            break;

        case 'INTEGER':
            retVal = integerRenderer( value, params.valueUnit, rawMode );
            break;

        case 'IMAGEDATA':
            retVal = imageRenderer( value, rawMode, params.onlyOneLine, unParsed );
            break;

        case 'JSON':
            retVal = jsonRenderer( value, params.filterString, rawMode, params.onlyOneLine, unParsed );
            break;

        case 'LINK':
            retVal = linkRenderer( value, params.filterString, params.editMode, rawMode, unParsed );
            break;

        case 'PHONE':
            retVal = phoneRenderer( value, params.filterString, params.editMode, rawMode, unParsed );
            break;

        case 'REFDIRECTION':
            retVal = directionRenderer( value, rawMode, params.showAdditionalRawNames );
            break;

        case 'REFTYPEWITHDIRECTION':
            parts = value.split( '::' );

            if ( parts?.length === 2 ) {
                retVal = directionRenderer( parts[ 0 ] as 'both' | 'dest' | 'src', false, false, false ).concat( ' ' ).concat( parts[ 1 ] ).concat( ' ' ).concat( directionRenderer( parts[ 0 ] as 'both' | 'dest' | 'src', false, false, false ) );
            }
            else {
                retVal = value;
            }
            break;

        case 'STATUS':
        case 'STATUSID':
        case 'STATUSNAME':
            retVal = statusRenderer( value, params.schema, rawMode, params.showAdditionalRawNames );
            break;

        case 'TIME':
            retVal = timeRenderer( value, params.timezone, rawMode );
            break;

        case 'TIMERULE':
            retVal = timeruleRenderer( value, params.timezone, params.filterString, params.language, rawMode );
            break;

        case 'TIMESTAMP':
            retVal = timestampRenderer( value, params.timezone, rawMode );
            break;

        case 'TEXT':
            retVal = textRenderer( value, params.filterString, rawMode, params.onlyOneLine, unParsed );
            break;

        case 'STRING':
        default:
            retVal = stringRenderer( value, params.filterString, unParsed );
            break;
    }

    return retVal;
}

/**
 * Renderer for boolean
 *
 * @param {String} value                            The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {string | JSX.Element}
 */
export const boolRenderer = ( value : any, rawMode : boolean = false, unParsed : boolean = false ) : string | JSX.Element => {
    let retVal : string | JSX.Element;

    if ( !rawMode ) {
        let className = 'fas yn-bool ';

        value = htmlQuoteEncode( value, true, true );
        value = formatBool( value );

        if ( value === 'true' ) {
            className += 'fa-check-circle yn-bool-true';
        }
        else if ( value === 'false' ) {
            className += 'fa-times-circle yn-bool-false';
        }
        else {
            className += 'fa-bullseye yn-bool-null';
        }

        if ( unParsed ) {
            retVal = `<span class='${className}'></span>`;
        }
        else {
            retVal = <span className={className}></span>;
        }
    }
    else {
        retVal = convertValueToString( value, 'BOOLEAN' );
    }

    return retVal;
}

/**
 * Renderer for color
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const colorRenderer = ( value : string, filterString : string = '', rawMode : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    retVal = markFilterSearchesInText( retVal, filterString );

    if ( !rawMode ) {
        retVal = `<div class="yn-color-preview" style="background-color: ${value}; color: ${getTextColorForBackgroundColor( value )}">${retVal}</div>`;
    }

    if ( !unParsed && ( !rawMode || filterString ) ) {
        retVal = parse( retVal );
    }

    return retVal;
}

/**
 * Renderer for coordinate
 *
 * @param {number[]} value                          The data value to be rendered
 * @param {boolean} [editMode=false]
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const coordinateRenderer = ( value : number[], editMode : boolean = false, rawMode : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value.join( ',' );

    if ( !rawMode && !editMode ) {
        retVal = `<a href="https://google.com/maps/place/${retVal}" target="_blank" style="color: inherit;"><span class="fa fas fa-map-marked"></span> ${retVal}</a>`;
    }
    else if ( !rawMode && editMode ) {
        retVal = `<span style="text-decoration: underline;"><span class="fa fas fa-map-marked"></span> ${retVal}</span>`;
    }

    if ( !unParsed && !rawMode && typeof retVal === 'string' ) {
        retVal = parse( retVal );
    }

    return retVal;
}

/**
 * Renderer for direction
 *
 * @param {'asc'|'desc'|'src'|'dest'} value         The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {Boolean} [showAdditionalRawNames=false]  Show raw names additionally
 * @param {Boolean} [showLabel=true]                Show direction label
 *
 * @return {( string | JSX.Element )[]}
 */
export const directionRenderer = ( value : 'asc' | 'desc' | 'src' | 'dest' | 'both', rawMode : boolean = false, showAdditionalRawNames : boolean = false, showLabel : boolean = true ) : ( string | JSX.Element )[] => {
    let retVal : ( string | JSX.Element )[] = [ value ],
        displayValue,
        className                           = 'fas yn-dir ';

    if ( !rawMode ) {
        displayValue = __( `YN.base.common.direction.${value}` );

        if ( showAdditionalRawNames ) {
            displayValue += ` (${value})`
        }

        switch ( value ) {
            case 'asc':
                className += 'fa-arrow-up yn-dir-asc';
                break;

            case 'desc':
                className += 'fa-arrow-down yn-dir-down';
                break;

            case 'both':
                className += 'fa-redo yn-dir-both yn-danger';
                displayValue = <span className="yn-danger">{displayValue}</span>;
                break;

            case 'src':
                className += 'fa-arrow-left yn-dir-src';
                break;

            case 'dest':
                className += 'fa-arrow-right yn-dir-dest';
                break;
        }

        retVal = [
            <div key="0" className={className}></div>
        ];

        if ( showLabel ) {
            retVal.push( displayValue );
        }
    }

    return retVal;
}

/**
 * Renderer for email
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [editMode=false]
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const emailRenderer = ( value : string, filterString : string = '', editMode : boolean = false, rawMode : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    retVal = emailRendererOrig( retVal, editMode, rawMode );

    retVal = markFilterSearchesInText( retVal, filterString, true );

    if ( !unParsed && ( !rawMode || filterString ) ) {
        retVal = parse( retVal );
    }

    return retVal;
}

/**
 * Renderer for IBAN
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const ibanRenderer = ( value : string, filterString : string = '', rawMode : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    retVal = markFilterSearchesInText( retVal, filterString.replace( / /g, '' ) );

    retVal = ibanRendererOrig( retVal, rawMode );

    if ( !unParsed && filterString ) {
        retVal = parse( retVal );
    }

    return retVal;
}

/**
 * Renderer for image
 *
 * @param {String} value                            The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [onlyOneLine=false]             Show value as one line
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {string | JSX.Element}
 */
export function imageRenderer( value : string, rawMode? : undefined, onlyOneLine? : boolean, unParsed? : boolean ) : JSX.Element;
export function imageRenderer<B extends boolean>( value : string, rawMode? : B, onlyOneLine? : boolean, unParsed? : boolean ) : B extends true ? string : JSX.Element;
export function imageRenderer( value : string, rawMode : boolean = false, onlyOneLine : boolean = false, unParsed : boolean = false ) : string | JSX.Element {
    let retVal : string | JSX.Element = imageRendererOrig( value, rawMode, onlyOneLine );

    if ( !unParsed && !rawMode ) {
        retVal = parse( retVal ) as JSX.Element;
    }

    return retVal;
}

/**
 * Renderer for json
 *
 * @param {String} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [onlyOneLine=false]             Show value as one line
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const jsonRenderer = ( value : string, filterString : string = '', rawMode : boolean = false, onlyOneLine : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = jsonRendererOrig( value, rawMode, onlyOneLine );

    retVal = markFilterSearchesInText( retVal, filterString );

    if ( !unParsed && ( !rawMode || filterString ) ) {
        retVal = parse( retVal );
    }

    return retVal;
}

/**
 * Renderer for link
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [editMode=false]
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const linkRenderer = ( value : string, filterString : string = '', editMode : boolean = false, rawMode : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    retVal = linkRendererOrig( retVal, editMode, rawMode );

    retVal = markFilterSearchesInText( retVal, filterString.replace( / /g, '' ), true );

    if ( !unParsed && ( !rawMode || filterString ) ) {
        retVal = parse( retVal );
    }

    return retVal;
}

/**
 * Renderer for phone
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [editMode=false]
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const phoneRenderer = ( value : string, filterString : string = '', editMode : boolean = false, rawMode : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    retVal = phoneRendererOrig( retVal, editMode, rawMode );

    retVal = markFilterSearchesInText( retVal, filterString.replace( / /g, '' ), true );

    if ( !unParsed && ( !rawMode || filterString ) ) {
        retVal = parse( retVal );
    }

    return retVal;
}

/**
 * renderer for status
 *
 * @param {String} value                            The data value to be rendered
 * @param {String} schema                           The schema the value belongs to
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {Boolean} [showAdditionalRawNames=false]  Show raw names additionally
 *
 * @return {string}
 */
export const statusRenderer = ( value : string | number, schema : Schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string => {
    return getStatusDisplayName( value, schema, rawMode, showAdditionalRawNames );
}

/**
 * renderer for string
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const stringRenderer = ( value : string, filterString : string = '', unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    if ( !unParsed && filterString ) {
        retVal = parse( markFilterSearchesInText( htmlQuoteEncode( retVal ), filterString ) );
    }

    return retVal;
}

export const isInvalidHTML = ( html : string ) => {
    const parser = new DOMParser(),
          doc    = parser.parseFromString( `<div>${html.replaceAll( '&nbsp;', ' ' )}</div>`, 'text/html' );

    if ( doc.documentElement.querySelector( 'parsererror' ) ) {
        return ( doc.documentElement.querySelector( 'parsererror' ) as HTMLElement ).innerText;
    }
    else {
        return false;
    }
}

export const stripHTMLtags = ( html : string ) => {
    const parser = new DOMParser(),
          doc    = parser.parseFromString( `<div>${html.replaceAll( '&nbsp;', ' ' )}</div>`, 'text/html' );

    if ( doc.documentElement.querySelector( 'parsererror' ) ) {
        return html;
    }
    else {
        return doc.documentElement.textContent ?? html;
    }
}

/**
 * renderer for text
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} filterString
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [onlyOneLine=false]             Show value as one line
 * @param {boolean} [unParsed=false]                Don't parse the return value
 *
 * @return {( string | JSX.Element )[]}
 */
export const textRenderer = ( value : string, filterString : string = '', rawMode : boolean = false, onlyOneLine : boolean = false, unParsed : boolean = false ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    if ( ( rawMode && filterString ) || isInvalidHTML( retVal ) ) {
        retVal = htmlQuoteEncode( retVal );
    }

    if ( filterString ) {
        retVal = markFilterSearchesInText( retVal, filterString, true );
    }

    if ( !rawMode || filterString ) {
        if ( !unParsed ) {
            if ( !isInvalidHTML( retVal ) ) {
                retVal = parse( retVal );
            }

            retVal = <span className={`yn-rendered-text ${onlyOneLine ? 'yn-one-line' : ''}`}>{retVal}</span>;
        }
        else {
            retVal = `<span class='yn-rendered-text ${onlyOneLine ? 'yn-one-line' : ''}'>${retVal}</span>`;
        }
    }

    return retVal;
}

/**
 * Renderer for timerule
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} [timezone='Europe/Berlin']       The timezone to convert the timestamp to
 * @param {string} filterString
 * @param {string} language                         The language identifier to convert the timestamp with
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string | ( string | JSX.Element )[]}
 */
export const timeruleRenderer = ( value : string, timezone : string = 'Europe/Berlin', filterString : string = '', language? : string, rawMode : boolean = false ) : string | ( string | JSX.Element )[] => {
    let retVal : string | ( string | JSX.Element )[];

    if ( !rawMode ) {
        retVal = formatTimerule( value, timezone, language );
    }
    else {
        retVal = [];

        const valueLines = value.split( /\r?\n/img );

        for ( let linesCount = 0; linesCount < valueLines.length; linesCount++ ) {
            retVal = retVal.concat( markFilterSearchesInText( htmlQuoteEncode( valueLines[ linesCount ] ), filterString ) );
            if ( linesCount !== valueLines.length - 1 ) {
                retVal.push( <br/> );
            }
        }
    }

    return retVal;
}

/**
 *
 * @param value                     The data value to be rendered
 * @param format
 * @param {string} filterString
 */
export const formatRenderer = ( value : string, format : any, filterString : string = '' ) : string | JSX.Element | JSX.Element[] => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    switch ( format.type ) {
        case 'Color':
            retVal = colorRenderer( value, filterString );
            break;

        case 'Custom':
            retVal = format.fnc( value );
            break;

        default:
            retVal = formatValue( value, format );
    }

    if ( typeof retVal === 'string' && format.type !== 'Color' ) {
        if ( filterString ) {
            retVal = markFilterSearchesInText( retVal, filterString );
        }
        retVal = parse( __( retVal, format.data ) );
    }

    return retVal;
}

/**
 * Applies the style or class to the textToStyle if the condition resolves to true.
 * Styles are applied by creating a stylesheet containing a class with the given styles and applying this class to the textToStyle.
 *
 * @param {string} textToStyle
 * @param {Style[]} conditionalStyles
 * @param {DataDescriptorValues} values
 * @param {string} [styleClassNamePrefix='dyn']
 *
 * @return {string | JSX.Element}
 */
export const stylingRenderer = ( textToStyle : string, conditionalStyles : Style[], values : DataDescriptorValues, styleClassNamePrefix : string = 'dyn' ) : string | JSX.Element => {
    let retVal : string | JSX.Element = textToStyle;

    const classNames = resolveStyleToClassNames( conditionalStyles, values, styleClassNamePrefix );

    if ( classNames.length ) {
        retVal = <span className={classNames.join( ' ' )}>{retVal}</span>;
    }

    return retVal;
}

/**
 * Selector template for the style of a **button**.
 *
 * A rule on the generated class alone never reaches a button: the theme styles it with two or three
 * classes - PrimeReact's `.p-button.p-button-text` carries the text colour, `.yn-buttonfield.p-button
 * .p-button-text` the surface - and both out-specify a single class. Anchoring the generated rule on
 * the same classes gives it equal specificity, and the generated sheet is appended last, so it wins.
 *
 * Pass it as `styleSelectors` wherever a BUTTON's own style is resolved ( form and designer canvas ).
 */
export const buttonStyleSelectors = [ '.yn-buttonfield.p-button.${className}' ];

/**
 * Selector template for the style of a whole **entry of an EnumField**.
 *
 * A single generated class reaches the row of a field list, but neither the row of a grid nor a
 * chip: the striped DataTable paints through `.p-datatable-striped .p-datatable-tbody > tr` and a
 * chip through `.p-chips .p-chips-multiple-container .p-chips-token` - both out-specify one class,
 * and a background on the row of a grid is covered by its cells anyway. The templates below carry
 * at least the same weight and the generated sheet is appended last, so the configured style wins
 * where it is meant to.
 *
 * Pass it as `styleSelectors` wherever the **rowStyle** of an enum entry is resolved.
 */
export const enumEntryRowStyleSelectors = [
    '.${className}',
    '.p-datatable-tbody > tr.${className}',
    '.p-datatable-tbody > tr.${className} > td',
    '.p-chips .p-chips-multiple-container .p-chips-token.${className}'
];

/**
 * @param {Style[]} conditionalStyles
 * @param {DataDescriptorValues} values
 * @param {string} [styleClassNamePrefix='dyn']
 * @param {string[]} [styleSelectors=[ '.${className}' ]]
 * @param {boolean} [noWidthAttributes=false]
 *
 * @return {string[]}
 */
export const resolveStyleToClassNames = ( conditionalStyles : Style[], values : DataDescriptorValues, styleClassNamePrefix : string = 'dyn', styleSelectors : string[] = [ '.${className}' ], noWidthAttributes : boolean = false ) : string[] => {
    const styleSheet = getOrCreateStyleSheet();
    let retVal : string[] = [];

    if ( styleSheet ) {
        for ( const conditionalStyle of conditionalStyles ) {
            let styleClassName;

            if ( conditionalStyle.style ) {
                styleClassName = `${styleClassNamePrefix}_${md5( ( JSON.stringify( { style: conditionalStyle.style, values } ) ) )}`;

                for ( const styleSelectorTemplate of styleSelectors ) {
                    // dynamic template string
                    const styleSelector = new Function( 'className', `return \`${styleSelectorTemplate}\`;` )( styleClassName );

                    if ( !ruleExists( styleSheet, `${styleSelector}` ) ) {
                        let ruleContent     = '{\n',
                            darkRuleContent = '{\n';

                        for ( const style of Object.keys( conditionalStyle.style ) as string[] ) {
                            let dark               = false,
                                styleConditionName = _.kebabCase( style );

                            if ( style.indexOf( 'dark__' ) === 0 ) {
                                dark = true;
                                styleConditionName = _.kebabCase( style.substring( 6 ) );
                            }

                            if (
                                !noWidthAttributes
                                || (
                                    styleConditionName !== 'width'
                                    && styleConditionName !== 'min-width'
                                    && styleConditionName !== 'max-width'
                                )
                            ) {
                                let styleConditionValue = conditionalStyle.style[ style ];

                                // replace value placeholders
                                for ( const key of Object.keys( values ) ) {
                                    styleConditionValue = styleConditionValue.replace( `\${${key}}`, values[ key ] );
                                }

                                if ( dark ) {
                                    darkRuleContent += `   ${styleConditionName}: ${styleConditionValue};\n`;
                                }
                                else {
                                    ruleContent += `   ${styleConditionName}: ${styleConditionValue};\n`;
                                }
                            }
                        }

                        ruleContent += '}\n';
                        darkRuleContent += '}\n';

                        appendRule( styleSheet, `${styleSelector}`, ruleContent );

                        if ( darkRuleContent.length > 5 ) {
                            const darkMode = getOrCreateMediaRule( styleSheet, '(prefers-color-scheme: dark)' );

                            if ( darkMode ) {
                                appendMediaRuleIfNotExists( darkMode, `${styleSelector}`, darkRuleContent );
                            }
                        }
                    }
                }
            }

            if ( !conditionalStyle.condition || resolveConditionRecursive( conditionalStyle.condition, values ) ) {
                if ( conditionalStyle.className ) {
                    retVal = retVal.concat( conditionalStyle.className.split( ' ' ) );
                }

                if ( conditionalStyle.style && styleClassName ) {
                    retVal.push( styleClassName );
                }
            }
        }
    }

    return retVal;
}

/**
 * @param {Style[]} conditionalStyles
 * @param {DataDescriptorValues} values
 *
 * @return {{ [attribute:string ]:string }}
 */
export const resolveStyle = ( conditionalStyles : Style[], values : DataDescriptorValues ) : { [ attribute : string ] : string } => {
    const retVal : { [ attribute : string ] : string } = {};

    for ( const conditionalStyle of conditionalStyles ) {
        if ( conditionalStyle.style && ( !conditionalStyle.condition || resolveConditionRecursive( conditionalStyle.condition, values ) ) ) {
            for ( const style of Object.keys( conditionalStyle.style ) as string[] ) {
                const styleConditionName = _.camelCase( style );

                retVal[ styleConditionName ] = conditionalStyle.style[ style ];
            }
        }
    }

    return retVal;
}
